import { useEffect, useRef, useState } from 'react';
import type { ChatMessageType } from '@/store/types';

interface QuestionRailProps {
  questions: ChatMessageType[];
  onQuestionClick: (messageId: string) => void;
}

const CLOSE_DELAY_MS = 280;

const truncateQuestion = (text: string, maxLength = 72) => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}…`;
};

export default function QuestionRail({ questions, onQuestionClick }: QuestionRailProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPanel = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <nav
      className={`aida-question-rail ${isOpen ? 'aida-question-rail--open' : ''}`}
      aria-label="Navigate your questions"
      onMouseEnter={openPanel}
      onMouseLeave={scheduleClose}
      onFocus={openPanel}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          scheduleClose();
        }
      }}
    >
      <div className="aida-question-rail__cluster">
        <div className="aida-question-rail__panel" aria-hidden={!isOpen}>
          <p className="aida-question-rail__heading">Your questions</p>
          <ol className="aida-question-rail__list">
            {questions.map((question, index) => (
              <li key={question.id}>
                <button
                  type="button"
                  className="aida-question-rail__item"
                  onClick={() => onQuestionClick(question.id)}
                  title={question.message}
                  tabIndex={isOpen ? 0 : -1}
                >
                  <span className="aida-question-rail__index">{index + 1}</span>
                  <span className="aida-question-rail__text">{truncateQuestion(question.message)}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>

        <div className="aida-question-rail__marks" aria-hidden="true">
          {questions.map(question => (
            <span key={question.id} className="aida-question-rail__mark" />
          ))}
        </div>
      </div>
    </nav>
  );
}
